import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { allowanceService } from '../../services/allowance.service';

interface RejectAllowanceModalProps {
  isOpen: boolean;
  allowanceId: number | null;
  onClose: () => void;
}

const schema = z.object({
  reason: z.string().min(1, 'Reason is required'),
});

type FormInput = z.input<typeof schema>;

export const RejectAllowanceModal: React.FC<RejectAllowanceModalProps> = ({
  isOpen,
  allowanceId,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!isOpen) {
      reset({});
    }
  }, [isOpen, reset]);

  const mutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => allowanceService.reject(id, reason),
    onSuccess: async () => {
      toast.success('Allowance rejected');
      await queryClient.invalidateQueries({ queryKey: ['allowances'] });
      onClose();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to reject allowance');
    },
  });

  const onSubmit = (data: FormInput) => {
    if (!allowanceId) return;
    const parsed = schema.parse(data);
    mutation.mutate({ id: allowanceId, reason: parsed.reason });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Reject Allowance"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            Reject
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="Reason"
          required
          placeholder="e.g., Incorrect calculation"
          error={errors.reason?.message}
          {...register('reason')}
        />
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
};
